// Tiny client-side date utilities. The server's lib/dateUtils.ts
// is the canonical implementation; this is a minimal subset for
// the parts the UI needs (input parsing, display formatting).
// Where the server has parseHM/computeDuration/dayCode, the
// client either gets those values from the API or doesn't need
// them at all.

export function todayYmd(): string {
  // Local-time today. The server's "today" is Africa/Algiers but
  // the UI's date picker is browser-local, which is fine because
  // the picker just selects a YYYY-MM-DD string — no timezone
  // arithmetic on the client side.
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function addDaysToYmd(ymd: string, n: number): string {
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(Date.UTC(y!, m! - 1, d!));
  dt.setUTCDate(dt.getUTCDate() + n);
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${String(dt.getUTCDate()).padStart(2, '0')}`;
}

const FR_LONG = new Intl.DateTimeFormat('fr-FR', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
});

export function fmtFrLong(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  return FR_LONG.format(new Date(Date.UTC(y!, m! - 1, d!)));
}

// Abbreviated weekday variant — "mer. 13 mai 2026" (proto arbitrage topbar).
const FR_SHORT_DOW = new Intl.DateTimeFormat('fr-FR', {
  weekday: 'short',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
});

export function fmtFrShortDow(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  return FR_SHORT_DOW.format(new Date(Date.UTC(y!, m! - 1, d!)));
}

export function isValidYmd(s: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(s) && !Number.isNaN(new Date(`${s}T00:00:00Z`).getTime());
}

// ─── Month helpers (YYYY-MM) — for the monthly-grid month picker ──

export function currentYm(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export function isValidYm(s: string): boolean {
  return /^\d{4}-\d{2}$/.test(s);
}

export function addMonthsToYm(ym: string, n: number): string {
  const [y, m] = ym.split('-').map(Number);
  const dt = new Date(Date.UTC(y!, m! - 1 + n, 1));
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}`;
}

const FR_MONTH = new Intl.DateTimeFormat('fr-FR', { month: 'long', year: 'numeric', timeZone: 'UTC' });

export function fmtFrMonth(ym: string): string {
  const [y, m] = ym.split('-').map(Number);
  return FR_MONTH.format(new Date(Date.UTC(y!, m! - 1, 1)));
}
